import { onErrorShowMessage } from '~src/errors';
import Swal from 'sweetalert2';
import { getUser } from '~src/user';
import {
  fetchCreditModules,
  createCheckoutSession,
  processDirectPayment,
  checkPaymentMethods,
  showCheckoutModal,
} from '~src/~core/payments';
import { trackEvent } from '~src/tracking';
import { saveEditorState } from '~core';
import { fetchAiCredits } from '~graphql/controllers';

/**
 * Shows module selection dialog
 * @param {Array} modules - Available credit modules
 * @returns {Promise<Object|null>} Selected module info or null if cancelled
 */
const showModuleSelectionDialog = async (modules, dialogOptions) => {
  const preselectedModuleId = String(dialogOptions?.inputValue || '').trim();
  if (preselectedModuleId) {
    const preselectedModule = modules.find((module) => {
      return module.objectId === preselectedModuleId;
    });

    if (!preselectedModule) {
      return null;
    }

    const confirmResult = await Swal.fire({
      title: dialogOptions.title || 'Conferma acquisto',
      html: dialogOptions.html,
      icon: dialogOptions.icon,
      showCancelButton:
        typeof dialogOptions.showCancelButton === 'boolean'
          ? dialogOptions.showCancelButton
          : true,
      cancelButtonText: dialogOptions.cancelButtonText || 'Annulla',
      confirmButtonText:
        dialogOptions.confirmButtonText || 'Procedi al pagamento',
      confirmButtonColor: dialogOptions.confirmButtonColor || '#278e8e',
      customClass: dialogOptions.customClass,
      reverseButtons: dialogOptions.reverseButtons,
    });

    if (!confirmResult.isConfirmed) {
      return null;
    }

    return {
      selectedModule: preselectedModule,
      selectedModuleId: preselectedModuleId,
    };
  }

  const inputOptions = {};
  if (modules && modules.length > 0) {
    modules.forEach((module) => {
      const totalCost = module.price_for_quantity * module.included_quantity;
      const totalPrice = totalCost.toFixed(2);
      inputOptions[module.objectId] = `${module.name} - €${totalPrice} i.e. (${
        module.included_quantity
      } ${module.included_quantity === 1 ? 'credito' : 'crediti'})`;
    });
  }

  const { value: selectedModuleId } = await Swal.fire({
    title: 'Seleziona un pacchetto di crediti',
    input: 'select',
    inputOptions: inputOptions,
    inputPlaceholder: 'Seleziona un pacchetto',
    showCancelButton: true,
    cancelButtonText: 'Annulla',
    confirmButtonText: 'Procedi al pagamento',
    confirmButtonColor: '#278e8e',
    customClass: {
      popup: 'ai-credits-module-popup',
      input: 'ai-credits-module-select',
      validationMessage: 'ai-credits-module-validation',
    },
    inputValidator: (value) => {
      return new Promise((resolve) => {
        if (value) {
          resolve();
        } else {
          resolve('Devi selezionare un pacchetto di crediti');
        }
      });
    },
    ...dialogOptions,
  });

  if (!selectedModuleId) return null;

  const selectedModule = modules.find(
    (module) => module.objectId === selectedModuleId
  );

  return { selectedModuleId, selectedModule };
};

/**
 * Shows payment method selection dialog and proceeds to checkout
 * This function can be called recursively when user clicks "back" in checkout modal
 */
const showPaymentMethodSelectionAndCheckout = async (
  selectedModuleId,
  selectedModule,
  user,
  totalCredits
) => {
  Swal.close();

  const paymentMethodResult = await Swal.fire({
    title: 'Come vuoi pagare?',
    html: `
      <div class="payment-type-container">
        <div class="payment-type-option" data-type="card">
          <input type="radio" name="paymentType" id="type-card" value="card" checked>
          <label for="type-card">
            <div class="payment-type-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
                <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1H2zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7z"/>
                <path d="M2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-1z"/>
              </svg>
            </div>
            <div class="payment-type-info">
              <strong>Carta di credito/debito</strong>
              <small>Accredito automatico al completamento della procedura</small>
            </div>
          </label>
        </div>
        <div class="payment-type-option" data-type="sepa_debit">
          <input type="radio" name="paymentType" id="type-sepa" value="sepa_debit">
          <label for="type-sepa">
            <div class="payment-type-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 .95 14.61 4h.89a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5H15v7a.5.5 0 0 1 .485.379l.5 2A.5.5 0 0 1 15.5 17H.5a.5.5 0 0 1-.485-.621l.5-2A.5.5 0 0 1 1 14V7H.5a.5.5 0 0 1-.5-.5v-2A.5.5 0 0 1 .5 4h.89L8 .95zM3.776 4h8.447L8 2.05 3.776 4zM2 7v7h1V7H2zm2 0v7h2.5V7H4zm3.5 0v7h1V7h-1zm2 0v7H12V7H9.5zM13 7v7h1V7h-1zm2-1V5H1v1h14zm-.39 9H1.39l-.25 1h13.72l-.25-1z"/>
              </svg>
            </div>
            <div class="payment-type-info">
              <strong>Addebito SEPA</strong>
              <small>Accredito automatico al completamento della procedura</small>
            </div>
          </label>
        </div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Continua',
    cancelButtonText: 'Annulla',
    confirmButtonColor: '#278e8e',
    customClass: {
      popup: 'payment-type-popup',
    },
    preConfirm: () => {
      const selected = document.querySelector(
        'input[name="paymentType"]:checked'
      );
      return selected ? selected.value : 'card';
    },
  });

  if (!paymentMethodResult.isConfirmed) {
    trackEvent('AI Credits Buy', {
      step: 'reject_payment_method_selection',
      balance: totalCredits,
    });
    return;
  }

  const selectedPaymentType = paymentMethodResult.value;

  trackEvent('AI Credits Buy', {
    step: 'select_payment_method_type',
    payment_type: selectedPaymentType,
    balance: totalCredits,
  });

  // Show loading
  Swal.fire({
    title: 'Caricamento in corso...',
    text: 'Stiamo preparando la tua pagina di pagamento',
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showConfirmButton: false,
    confirmButtonColor: '#278e8e',
    willOpen: () => {
      Swal.showLoading();
    },
  });

  const checkoutSession = await createCheckoutSession(selectedModuleId, user, {
    paymentMethodType: selectedPaymentType,
  });
  if (!checkoutSession) return;

  trackEvent('AI Credits Buy', {
    step: 'create_checkout_session',
    payment_type: selectedPaymentType,
    balance: totalCredits,
  });

  // we force the save of the project because when the stripe checkout session is successful, it will reload the page
  await saveEditorState(true, false);
  await showCheckoutModal(checkoutSession, selectedModule, totalCredits, {
    onBack: () => {
      // Recursively call this function to show payment method selection again
      showPaymentMethodSelectionAndCheckout(
        selectedModuleId,
        selectedModule,
        user,
        totalCredits
      );
    },
  });
};

/**
 * Main function to handle AI credits purchase
 */
export const handleBuyAiCredits = async (dialogOptions = {}) => {
  Swal.showLoading();

  let credits;
  try {
    credits = await fetchAiCredits();
  } catch (error) {
    Swal.hideLoading();
    onErrorShowMessage(false, {
      icon: 'error',
      title: 'Errore',
      text:
        'Si è verificato un errore durante il caricamento del saldo crediti.',
      confirmButtonColor: '#278e8e',
    });
    return;
  }

  // if the org has more than 100 credits, we ask if they are sure they want to buy more
  const totalCredits =
    credits.edges[0].freeCreditsAmount + credits.edges[0].paidCreditsAmount;

  if (totalCredits > 100) {
    const result = await Swal.fire({
      title: `Hai già ${totalCredits} crediti, sei sicuro di volerne comprare altri?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Si',
      cancelButtonText: 'No',
      confirmButtonColor: '#278e8e',
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    if (!result.isConfirmed) {
      trackEvent('AI Credits Buy', {
        step: 'too_many_credits_stop',
        balance: totalCredits,
      });
      return;
    } else {
      trackEvent('AI Credits Buy', {
        step: 'too_many_credits_continue',
        balance: totalCredits,
      });
    }
  }

  let modules;

  try {
    // Step 1: Fetch available credit modules
    modules = await fetchCreditModules();
    if (!modules) return;
  } catch (error) {
    Swal.hideLoading();
    onErrorShowMessage(false, {
      icon: 'error',
      title: 'Errore',
      text:
        'Si è verificato un errore durante il caricamento del listino prezzi dei crediti.',
      confirmButtonColor: '#278e8e',
    });
    return;
  }

  trackEvent('AI Credits Buy', {
    step: 'display_offer',
    balance: totalCredits,
  });

  // Step 2: Show module selection dialog
  const selection = await showModuleSelectionDialog(modules, dialogOptions);
  if (!selection) {
    trackEvent('AI Credits Buy', {
      step: 'reject_offer',
      balance: totalCredits,
    });
    return;
  }

  trackEvent('AI Credits Buy', {
    step: 'continue_to_payment',
    balance: totalCredits,
  });

  const { selectedModuleId, selectedModule } = selection;

  Swal.fire({
    title: 'Caricamento in corso...',
    text: 'Stiamo preparando la tua pagina di pagamento',
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showConfirmButton: false,
    confirmButtonColor: '#278e8e',
    willOpen: () => {
      Swal.showLoading();
    },
  });

  try {
    // Step 3: Get user info
    const { user } = await getUser();

    // Step 4: Check if user has a Stripe customer ID
    if (user.org_pointer.stripe_customer_id) {
      // Step 4a: Check for saved payment methods
      const paymentMethodsResult = await checkPaymentMethods();
      if (
        paymentMethodsResult.paymentMethods &&
        paymentMethodsResult.paymentMethods.data.length > 0
      ) {
        // Show confirmation dialog based on number of payment methods
        const paymentMethods = paymentMethodsResult.paymentMethods.data;
        let selectedPaymentMethodId;

        trackEvent('AI Credits Buy', {
          step: 'display_existing_payment_method',
          payment_methods_count: paymentMethods.length,
          balance: totalCredits,
        });

        if (paymentMethods.length === 1) {
          // Case 1: Only one payment method - show details and confirm
          const card = paymentMethods[0].card;
          const result = await Swal.fire({
            title: 'Conferma Pagamento',
            html: `
              <div class="card-details">
                <p>Il pagamento verrà effettuato con la tua carta salvata:</p>
                <div class="card-info">
                  <strong>${card.brand.toUpperCase()}</strong> che termina con <strong>${
              card.last4
            }</strong>
                  <br>Scadenza: ${card.exp_month}/${card.exp_year}
                </div>
              </div>
            `,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Paga Ora',
            cancelButtonText: 'Cambia metodo di pagamento',
            confirmButtonColor: '#278e8e',
            reverseButtons: true,
          });

          if (result.isConfirmed) {
            selectedPaymentMethodId = paymentMethods[0].id;
            trackEvent('AI Credits Buy', {
              step: 'confirm_existing_payment_method',
              balance: totalCredits,
            });
          } else {
            Swal.close();
            trackEvent('AI Credits Buy', {
              step: 'reject_existing_payment_method',
              balance: totalCredits,
            });
            // fallback to checkout page
            Swal.fire({
              title: 'Caricamento in corso...',
              text: 'Stiamo preparando la tua pagina di pagamento',
              allowOutsideClick: false,
              allowEscapeKey: false,
              allowEnterKey: false,
              showConfirmButton: false,
              confirmButtonColor: '#278e8e',
              willOpen: () => {
                Swal.showLoading();
              },
            });
          }
        } else {
          // Case 2: Multiple payment methods - let user select one
          let optionsHtml = '';
          paymentMethods.forEach((method, index) => {
            const card = method.card;
            optionsHtml += `
              <div class="payment-option">
                <input type="radio" name="paymentMethod" id="method-${index}" value="${
              method.id
            }" ${index === 0 ? 'checked' : ''}>
                <label for="method-${index}">
                  ${card.brand.toUpperCase()} che termina con ${
              card.last4
            } (Scadenza: ${card.exp_month}/${card.exp_year})
                </label>
              </div>
            `;
          });

          const result = await Swal.fire({
            title: 'Seleziona Metodo di Pagamento',
            html: `
              <div class="payment-methods-container">
                ${optionsHtml}
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Paga Ora',
            cancelButtonText: 'Annulla',
            confirmButtonColor: '#278e8e',
            reverseButtons: true,
            preConfirm: () => {
              const selected = document.querySelector(
                'input[name="paymentMethod"]:checked'
              );
              return selected ? selected.value : null;
            },
          });

          if (result.isConfirmed && result.value) {
            selectedPaymentMethodId = result.value;
            trackEvent('AI Credits Buy', {
              step: 'confirm_existing_payment_method',
              balance: totalCredits,
            });
          } else {
            Swal.close();
            trackEvent('AI Credits Buy', {
              step: 'reject_existing_payment_method',
              balance: totalCredits,
            });
            return;
          }
        }

        // Proceed with payment using the selected payment method
        if (selectedPaymentMethodId) {
          const paymentResult = await processDirectPayment(
            [selectedModuleId],
            user.org_pointer.stripe_customer_id,
            selectedPaymentMethodId,
            totalCredits
          );
          if (paymentResult.success) {
            trackEvent('AI Credits Buy', {
              step: 'charge_payment_method',
              success: true,
              balance: totalCredits,
            });
            return; // Payment succeeded, we're done
          } else {
            trackEvent('AI Credits Buy', {
              step: 'charge_payment_method',
              success: false,
              balance: totalCredits,
            });
          }
          // If payment failed, fall through to checkout flow
        }
      }
    }

    // Step 5: Fallback to checkout flow - ask for payment method type
    await showPaymentMethodSelectionAndCheckout(
      selectedModuleId,
      selectedModule,
      user,
      totalCredits
    );

    // TODO: handle successful or unsuccessful checkout page result
  } catch (error) {
    Swal.hideLoading();
    onErrorShowMessage(false, {
      icon: 'error',
      title: 'Errore',
      text: 'Si è verificato un errore durante il processo di pagamento.',
      confirmButtonColor: '#278e8e',
    });
    console.error('Payment process error:', error);
  }
};
